/**
 * AppLayout — shell with sidebar, header, and content area.
 * Includes skip-to-content, error boundary, and accessible landmarks.
 */

import type { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { useAuth } from '@/features/auth/use-auth';
import { Button } from '@/components/ui/button';
import { LogOut } from 'lucide-react';
import { SkipToContent } from '@/components/shared/SkipToContent';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';

interface AppLayoutProps {
  children?: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { signOut, profile } = useAuth();

  return (
    <SidebarProvider>
      <SkipToContent />
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <header
            className="h-12 flex items-center justify-between border-b border-border bg-card px-4 shrink-0"
            role="banner"
          >
            <SidebarTrigger className="ml-0 min-h-[44px] min-w-[44px]" aria-label="Toggle sidebar" />
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground hidden sm:inline" aria-hidden="true">
                {profile?.email}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={signOut}
                aria-label="Sign out"
                className="min-h-[44px] min-w-[44px]"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </header>
          <main
            id="main-content"
            className="flex-1 overflow-auto"
            role="main"
            tabIndex={-1}
          >
            <ErrorBoundary>
              {children ?? <Outlet />}
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
